<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div id="blurred-background" v-if="isViewerLoading || loading">
      <v-progress-circular
        id="loading-indicator"
        :size="40"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <div id="display-options-checkboxes" ref="displayOptionsCheckboxes">
      <v-checkbox
        v-if="constellationData.payloads"
        :label="'Show Field Of Regard'"
        v-bind:value="viewerOptions.showFieldOfRegard"
        :dark="true"
        color="info"
        @click.native.prevent.stop.capture="checkboxClicked"
      ></v-checkbox>
      <v-dialog
        v-show="shouldDialogTrigger"
        v-model="viewerOptions.showDialog"
        persistent
        max-width="290"
      >
        <v-card>
          <v-card-title class="text-h5"> Are you sure? </v-card-title>
          <v-card-text>
            Your constellation contains many satellites. Displaying their field
            of regard will affect rendering performance and may even make your
            browser crash. Continue anyway?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              @click="
                () => {
                  viewerOptions.showFieldOfRegard = false
                  viewerOptions.showDialog = false
                }
              "
            >
              Cancel
            </v-btn>
            <v-btn
              color="blue darken-1"
              text
              @click="
                () => {
                  viewerOptions.showDialog = false
                  viewerOptions.showFieldOfRegard = true
                }
              "
            >
              Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- <v-checkbox
                  :label="'Show Swath'"
                  v-model="viewerOptions.showSwath"
                ></v-checkbox> -->
      <v-checkbox
        :label="'Show Timeline'"
        :dark="true"
        color="info"
        v-model="viewerOptions.showTimeline"
      ></v-checkbox>
    </div>
    <cesium-lighting-button ref="cesiumLightingButton" />
    <cesium-recorder ref="cesiumRecorder" />
  </div>
</template>

<script>
import * as Cesium from 'cesium'
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import 'vue-awesome/icons'
import Icon from 'vue-awesome/components/Icon'
import CesiumConstellationsTools from '../../components/ThreeD/CesiumConstellationsTools'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import CesiumLightingButton from '../../components/ThreeD/CesiumLightingButton.vue'

export default {
  name: 'constellation-viewer',
  props: ['constellationData', 'loading'],
  components: {
    'v-icon': Icon,
    'cesium-recorder': CesiumRecorder,
    'cesium-lighting-button': CesiumLightingButton
  },
  mixins: [CesiumInitialization],

  data() {
    return {
      viewerOptions: {
        isRecording: false,
        showDialog: false,
        showFieldOfRegard: false,
        // showSwath: false,
        showTimeline: false,
        sceneMode2D: false,
        morphButtonDisabled: false
      },
      entitiesList: [],
      isViewerLoading: false,
      creditsLeftOffset: 0,
      viewer: null,
      ellipsoid: null,
      czml: [],
      entities: null,
      rotatingOrbitalPlanes: null,
      swathPrimitivesCollection: null,
      FOREntities: [],
      chunks: [],
      rec: null,
      stream: null,
      icrfToFixed: null,
      intervalIndex: 0,
      satMagnitudes: [],
      lastUpdated: new Cesium.JulianDate()
    }
  },

  computed: {
    initialDate() {
      return this.constellationData.initialDate
    },

    // Computing final date in this way allows visualizing a perfect loop
    finalDate() {
      let finalDate = new Cesium.JulianDate()
      Cesium.JulianDate.addSeconds(
        Cesium.JulianDate.fromIso8601(this.initialDate),
        this.timeStep * this.constellationData.traces[0].length,
        finalDate
      )
      return Cesium.JulianDate.toIso8601(finalDate)
    },

    intraPlanePhasing() {
      return this.constellationData.intraPlanePhasing
    },

    interPlanePhasing() {
      return this.constellationData.interPlanePhasing
    },

    radius() {
      return this.constellationData.payloads
        .groundRangeAlongTrackOperatingAltitude
    },

    alongTrackDistance() {
      return this.constellationData.payloads
        .groundRangeAlongTrackOperatingAltitude
    },

    crossTrackDistance() {
      return this.constellationData.payloads
        .groundRangeCrossTrackOperatingAltitude
    },

    timeStep() {
      return Math.round(
        this.constellationData.period / this.constellationData.traces[0].length
      )
    },

    timeIntervals() {
      const startDate = Cesium.JulianDate.fromIso8601(this.initialDate)
      const endDate = Cesium.JulianDate.fromIso8601(this.finalDate)
      let intervalCollection = new Cesium.TimeIntervalCollection()
      let currDate = startDate.clone()
      let nextDate = new Cesium.JulianDate()
      // the addInterval method merges intervals with the same data. Hence the need to pass a different data object to each interval
      let i = 0
      while (
        Cesium.JulianDate.secondsDifference(endDate, currDate) > this.timeStep
      ) {
        Cesium.JulianDate.addSeconds(currDate, this.timeStep, nextDate)
        intervalCollection.addInterval(
          new Cesium.TimeInterval({
            start: currDate,
            stop: nextDate,
            data: i
          })
        )
        currDate = nextDate.clone()
        i++
      }
      return intervalCollection
    },

    shouldDialogTrigger() {
      return this.constellationData.numberOfPlanes *
        this.constellationData.numberOfSpacecraftsPerPlane >
        12
        ? true
        : false
    }
  },

  watch: {
    async constellationData(val) {
      if (val && !this.isViewerLoading) {
        this.isViewerLoading = true
        this.$emit('cesiumLoading', true)
        this.addDataSources()
        // used to place credits when toggling timeline display
        this.creditsLeftOffset = document.getElementsByClassName(
          'cesium-viewer-bottom'
        )[0].style.left
        this.$emit('cesiumLoading', false)
      }
    },
    'viewerOptions.showFieldOfRegard'(show) {
      for (let i = 0; i < this.FOREntities.length; i++) {
        this.viewer.entities.getById(this.FOREntities[i]).show = show
      }
    },
    // 'viewerOptions.showSwath'(val) {
    //   this.swathPrimitivesCollection.show = val
    // },
    'viewerOptions.showTimeline'(val) {
      this.toggleTimeline(val)
    },
    'viewerOptions.sceneMode2D'(val) {
      if (val === true) {
        this.morph2D()
      } else {
        this.morph3D()
      }
    }
  },

  mounted() {
    this.initializeViewer(true, true, true, false)
    this.ellipsoid = this.viewer.scene.globe.ellipsoid
    this.toggleTimeline(false)
    this.insertElementInToolbar(this.$refs.cesiumLightingButton.$el)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)
    this.insertElementInToolbar(this.$refs.displayOptionsCheckboxes)
    this.$refs.cesiumLightingButton.toggleLighting()
    this.hideSceneModePicker()
    // this.updateElementsStyle('#cesium-container')
    this.viewer.camera.flyHome(0)
  },

  methods: {
    /**
     * Create and add dataSources to the viewer
     */
    async addDataSources() {
      this.clearVisualElements().then(async () => {
        document.body.style.cursor = 'progress'
        this.setCesiumClock(
          Cesium.JulianDate.fromIso8601(this.initialDate),
          Cesium.JulianDate.fromIso8601(this.finalDate),
          Math.round(this.constellationData.period / 30)
        )
        this.viewer.clock.shouldAnimate = false
        await Cesium.Transforms.preloadIcrfFixed(
          Cesium.TimeInterval.fromIso8601({
            iso8601: this.initialDate + '/' + this.finalDate
          })
        )
        this.addPlanesAndSatellites()
        this.viewer.clock.onTick.addEventListener(this.computeIcrfToFixed)
        this.viewer.clock.shouldAnimate = true
        document.body.style.cursor = 'default'
        this.viewer.scene.postUpdate.addEventListener(this.makeEarthSpin)
        this.viewer.scene.morphComplete.addEventListener(this.onMorphComplete)
        // this.viewer.clock.onTick.addEventListener(
        //   this.updateVisibleSwathPrimitives
        // )
        this.isViewerLoading = false
      })
    },

    /**
     * Create the base czml data source and add it to the viewer
     */
    async addPlanesAndSatellites() {
      const colorsArray = CesiumTools.createColorsArray(
        this.constellationData.traces.length
      )

      this.rotatingOrbitalPlanes = new Cesium.PrimitiveCollection()
      // this.swathPrimitivesCollection = new Cesium.PrimitiveCollection()

      this.constellationData.traces.forEach((arr, planeIndex) => {
        const color = colorsArray[planeIndex]
        this.createOrbitalPlane(arr, color, planeIndex)

        for (
          let i = 0;
          i < this.constellationData.numberOfSpacecraftsPerPlane;
          i++
        ) {
          this.createSatellitePoint(arr, planeIndex, i, color)
        }
      })

      this.viewer.scene.primitives.add(this.rotatingOrbitalPlanes)
      // this.viewer.scene.primitives.add(this.swathPrimitivesCollection)
      // this.swathPrimitivesCollection.show = this.showSwath
    },

    /**
     * Used as a callback property, this function returns the position of its "parent" entity
     * @param entityCollection - the entityCollection which contains the "parent" entity
     * @param parentId - the id of the "parent" entity
     * @returns the position of the "parent" entity at clock's current time
     */
    updateEntityPositionFromParent(entityCollection, parentId) {
      let parentEntity = entityCollection.getById(parentId)
      if (!Cesium.defined(parentEntity)) {
        return
      }
      return parentEntity.position.getValue(this.viewer.clock.currentTime)
    },

    /**
     * Create a circular entity to represent the given satellite field of regard
     * @param satId - the id of the satellite
     * @param color - the rgb color array
     */
    createEllipticFieldOfRegard(satId, color) {
      return new Cesium.Entity({
        id: `fovCircle-${satId}`,
        show: this.viewerOptions.showFieldOfRegard,
        position: new Cesium.CallbackProperty(
          () =>
            this.updateEntityPositionFromParent(this.viewer.entities, satId),
          false
        ),
        ellipse: {
          semiMinorAxis: this.radius,
          semiMajorAxis: this.radius,
          height: 0,
          outline: true,
          outlineColor: color,
          fill: false
        }
      })
    },

    /**
     * Create a polyline entity to represent the given satellite field of regard
     * @param satId - the id of the satellite
     * @param color - the rgb color array
     */
    createRectangularFieldOfRegard(satId, color) {
      return new Cesium.Entity({
        id: `fovRectangle-${satId}`,
        show: this.viewerOptions.showFieldOfRegard,
        polyline: new Cesium.PolylineGraphics({
          clampToGround: false,
          material: color,
          positions: new Cesium.CallbackProperty(
            () => this.getPolylineCoordinates(satId),
            false
          ),
          width: 1
        })
      })
    },

    getPolylineCoordinates(id) {
      const retrieveSatPositions = (id) => {
        const currentTime = this.viewer.clock.currentTime
        const timePlusOne = new Cesium.JulianDate()
        Cesium.JulianDate.addSeconds(currentTime, 1, timePlusOne)

        const currSatPosInertial = this.viewer.entities
          .getById(id)
          .position.getValueInReferenceFrame(
            currentTime,
            Cesium.ReferenceFrame.INERTIAL
          )

        const nextSatPosInertial = this.viewer.entities
          .getById(id)
          .position.getValueInReferenceFrame(
            timePlusOne,
            Cesium.ReferenceFrame.INERTIAL
          )
        return [currSatPosInertial, nextSatPosInertial]
      }

      const checkConstantALTDirection = (id, altDirection) => {
        const numbersInId = id.match(/\d/g)
        const index =
          parseInt(numbersInId[0], 10) *
            this.constellationData.numberOfSpacecraftsPerPlane +
          parseInt(numbersInId[1], 10)
        let { firstMag, prevALTDirection } = this.satMagnitudes[index]
        const currMag = Cesium.Cartesian3.magnitude(altDirection)

        if (firstMag) {
          if (prevALTDirection) {
            if (Math.abs(currMag - firstMag) > 200) {
              prevALTDirection.clone(altDirection)
            }
          } else {
            this.satMagnitudes[index].prevALTDirection = altDirection.clone()
          }
        } else {
          this.satMagnitudes[index].firstMag = currMag
        }
      }

      const createDirectionVectors = (id) => {
        const alongTrackDirection = new Cesium.Cartesian3()
        const crossTrackDirection = new Cesium.Cartesian3()
        const downDirection = new Cesium.Cartesian3()

        Cesium.Cartesian3.subtract(
          nextSatPosInertial,
          currSatPosInertial,
          alongTrackDirection
        )

        // checkConstantALTDirection(id, alongTrackDirection)

        Cesium.Cartesian3.negate(currSatPosInertial, downDirection)

        Cesium.Cartesian3.cross(
          alongTrackDirection,
          currSatPosInertial,
          crossTrackDirection
        )
        return [alongTrackDirection, downDirection, crossTrackDirection]
      }

      const getNSD = () => {
        const alongTrackRay = new Cesium.Ray(
          currSatPosInertial,
          alongTrackDirection
        )

        const North = new Cesium.Cartesian3()
        const South = new Cesium.Cartesian3()
        const Down = new Cesium.Cartesian3()

        Cesium.Ray.getPoint(alongTrackRay, this.alongTrackDistance, North)
        Cesium.Ray.getPoint(alongTrackRay, -this.alongTrackDistance, South)
        return [North, South, Down]
      }

      const computeVertexPos = (ns, we) => {
        let posFixed = new Cesium.Cartesian3()
        let intPoint = new Cesium.Cartesian3()
        let crossTrackRay = null
        if (ns === 'North') {
          crossTrackRay = new Cesium.Ray(North, crossTrackDirection)
        } else {
          crossTrackRay = new Cesium.Ray(South, crossTrackDirection)
        }
        if (we === 'East') {
          Cesium.Ray.getPoint(crossTrackRay, this.crossTrackDistance, intPoint)
        } else {
          Cesium.Ray.getPoint(crossTrackRay, -this.crossTrackDistance, intPoint)
        }
        const downRay = new Cesium.Ray(intPoint, downDirection)
        const downDistance = Cesium.IntersectionTests.rayEllipsoid(
          downRay,
          this.ellipsoid
        ).start
        Cesium.Ray.getPoint(downRay, downDistance, Down)
        if (!Cesium.defined(this.icrfToFixed)) {
          return
        }
        Cesium.Matrix3.multiplyByVector(this.icrfToFixed, Down, posFixed)
        return posFixed
      }

      const [currSatPosInertial, nextSatPosInertial] = retrieveSatPositions(id)

      // avoid crashing before the end of simulation
      if (!currSatPosInertial || !nextSatPosInertial) {
        return
      }

      const [alongTrackDirection, downDirection, crossTrackDirection] =
        createDirectionVectors(id)

      const [North, South, Down] = getNSD()

      const NW = computeVertexPos('North', 'West')
      const NE = computeVertexPos('North', 'East')
      const SE = computeVertexPos('South', 'East')
      const SW = computeVertexPos('South', 'West')
      const finalPoints = [SW, SE, NE, NW, SW]

      return finalPoints
    },

    /**
     * Create an orbital plane using the given positions and rgb arrays
     * @param posArray - the positions array
     * @param color - the Cesium color
     */
    createOrbitalPlane(posArray, color) {
      let polylinePosArray = []

      for (let k = 0; k < posArray.length; k++) {
        const subArr = posArray[k]
        subArr.forEach((value) => polylinePosArray.push(value))
      }

      const polylineGeometry = new Cesium.PolylineGeometry({
        positions: Cesium.Cartesian3.unpackArray(polylinePosArray)
      })

      const polylineInstance = new Cesium.GeometryInstance({
        geometry: polylineGeometry,
        attributes: {
          color: Cesium.ColorGeometryInstanceAttribute.fromColor(color)
        }
      })

      const polylinePrimitive = new Cesium.Primitive({
        geometryInstances: polylineInstance,
        appearance: new Cesium.PolylineColorAppearance()
      })

      this.rotatingOrbitalPlanes.add(polylinePrimitive)
    },

    /**
     * Compute the positions of the satellite and create the visual elements to represent it in viewer
     * @param posArray - the array containing the positions of the orbital plane
     * @param planeIndex - the index of the plane of the satellite
     * @param satIndex - the index of the satellite on its plane
     * @param color - the Cesium color of the point
     * @returns the CZML point
     */
    createSatellitePoint(posArray, planeIndex, satIndex, color) {
      const totalShift = CesiumConstellationsTools.computeShift(
        posArray.length,
        planeIndex,
        satIndex,
        this.intraPlanePhasing,
        this.interPlanePhasing
      )
      const shiftedPosArray = CesiumConstellationsTools.createShiftedPosArray(
        posArray,
        totalShift,
        this.timeStep
      )
      const positionProperty =
        CesiumConstellationsTools.createSampledPositionProperty(
          shiftedPosArray,
          this.viewer.clock.startTime,
          Cesium.ReferenceFrame.INERTIAL
        )
      const satId = `sat-${planeIndex}-${satIndex}`
      this.addSatelliteEntity(positionProperty, satId, color)
      this.entitiesList.push(satId)

      if (!this.constellationData.payloads) {
        return
      }

      let fovType = this.constellationData.payloads.fieldOfViewType

      this.satMagnitudes.push({
        firstMag: undefined,
        prevALTDirection: undefined
      })
      if (fovType === 'TYPE_CIRCULAR') {
        let entity = this.viewer.entities.add(
          this.createEllipticFieldOfRegard(satId, color)
        )
        this.FOREntities.push(entity.id)
      } else if (fovType === 'TYPE_RECTANGULAR') {
        let entity = this.viewer.entities.add(
          this.createRectangularFieldOfRegard(satId, color)
        )
        this.FOREntities.push(entity.id)
        this.entitiesList.push(entity.id)
      }
    },

    /**
     * Create a CZML object representing a satellite
     * @param positionProperty - the time sampled positions of the point
     * @param satelliteId - the id of the new entity
     * @param color - the Cesium color of the point
     * @returns the CZML point
     */
    addSatelliteEntity(positionProperty, satelliteId, color) {
      this.viewer.entities.add(
        new Cesium.Entity({
          id: satelliteId,
          position: positionProperty,
          point: {
            pixelSize: 8,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 1,
            color: color
          }
        })
      )
    },

    createSwathPrimitive(shiftedPosArray, colorArray) {
      let corridorInstances = []
      let posArray = []
      let [r, g, b, __] = colorArray
      for (let k = 0; k < this.timeIntervals.length; k++) {
        for (
          let i = 0;
          i <
          Math.floor((k * shiftedPosArray.length) / this.timeIntervals.length);
          i += 128
        ) {
          const inertialCart = new Cesium.Cartesian3()
          const fixedCart = new Cesium.Cartesian3()
          const tmpDate = new Cesium.JulianDate()
          Cesium.JulianDate.addSeconds(
            Cesium.JulianDate.fromIso8601(this.initialDate),
            shiftedPosArray[i],
            tmpDate
          )
          const icrfToFixed =
            Cesium.Transforms.computeIcrfToFixedMatrix(tmpDate)
          Cesium.Cartesian3.unpack(shiftedPosArray, i + 1, inertialCart)
          Cesium.Matrix3.multiplyByVector(icrfToFixed, inertialCart, fixedCart)
          const downDirection = new Cesium.Cartesian3()
          Cesium.Cartesian3.negate(fixedCart, downDirection)
          const downRay = new Cesium.Ray(fixedCart, downDirection)
          const downDistance = Cesium.IntersectionTests.rayEllipsoid(
            downRay,
            this.ellipsoid
          ).start
          Cesium.Ray.getPoint(downRay, downDistance, fixedCart)
          posArray.push(fixedCart)
        }
        corridorInstances.push(
          new Cesium.GeometryInstance({
            id: k,
            geometry: new Cesium.CorridorGeometry({
              positions: posArray,
              width: this.crossTrackDistance * 2,
              ellipsoid: this.ellipsoid,
              vertexFormat: Cesium.EllipsoidSurfaceAppearance.VERTEX_FORMAT,
              cornerType: Cesium.CornerType.MITERED
            }),
            attributes: {
              color: new Cesium.ColorGeometryInstanceAttribute(
                r / 255,
                g / 255,
                b / 255,
                0.5
              ),
              show: new Cesium.ShowGeometryInstanceAttribute(false)
            }
          })
        )
        posArray = []
      }

      const corridorPrimitive = new Cesium.Primitive({
        geometryInstances: corridorInstances,
        appearance: new Cesium.PerInstanceColorAppearance({
          flat: true
        }),
        asynchronous: true
      })
      this.swathPrimitivesCollection.add(corridorPrimitive)
    },

    updateVisibleSwathPrimitives(clock) {
      const currTime = clock.currentTime
      const index = this.timeIntervals.indexOf(currTime)
      if (index !== this.intervalIndex) {
        for (let i = 0; i < this.swathPrimitivesCollection.length; i++) {
          const primitive = this.swathPrimitivesCollection.get(i)
          if (primitive.ready === true) {
            let attributes = primitive.getGeometryInstanceAttributes(
              this.intervalIndex
            )
            if (attributes) {
              attributes.show =
                Cesium.ShowGeometryInstanceAttribute.toValue(false)
            }
          }
        }
        this.intervalIndex = index
        for (let i = 0; i < this.swathPrimitivesCollection.length; i++) {
          const primitive = this.swathPrimitivesCollection.get(i)
          if (primitive.ready === true) {
            let attributes = primitive.getGeometryInstanceAttributes(
              this.intervalIndex
            )
            if (attributes) {
              attributes.show =
                Cesium.ShowGeometryInstanceAttribute.toValue(true)
            }
          }
        }
      }
    },

    /**
     * Remove event listener, primitives, entities and datasources from viewer
     */
    clearVisualElements() {
      return new Promise((resolve, reject) => {
        this.viewer.scene.postUpdate.removeEventListener(this.makeEarthSpin)
        this.viewer.clock.onTick.removeEventListener(this.computeIcrfToFixed)
        this.viewer.scene.morphComplete.removeEventListener(
          this.onMorphComplete
        )
        this.viewer.entities.removeAll()
        CesiumTools.removePrimitivesFromPC(this.rotatingOrbitalPlanes)
        CesiumTools.removePrimitivesFromPC(this.swathPrimitivesCollection)
        this.FOREntities = []
        this.entitiesList = []
        resolve()
      })
    },

    morph2D() {
      this.viewerOptions.morphButtonDisabled = true
      this.viewer.scene.postUpdate.removeEventListener(this.makeEarthSpin)
      this.viewer.clock.shouldAnimate = false
      this.rotatingOrbitalPlanes.show = false
      this.viewer.scene.morphTo2D()
    },

    morph3D() {
      this.viewer.clock.shouldAnimate = false
      this.viewer.scene.morphTo3D()
    },

    onMorphComplete() {
      this.viewerOptions.morphButtonDisabled = false
      const toggleUserInteraction = (value) => {
        this.viewer.scene.screenSpaceCameraController.enableRotate = value
        this.viewer.scene.screenSpaceCameraController.enableTranslate = value
        this.viewer.scene.screenSpaceCameraController.enableZoom = value
        this.viewer.scene.screenSpaceCameraController.enableTilt = value
        this.viewer.scene.screenSpaceCameraController.enableLook = value
      }
      if (this.viewer.scene.mode === Cesium.SceneMode.SCENE3D) {
        this.rotatingOrbitalPlanes.show = true
        this.viewer.scene.postUpdate.addEventListener(this.makeEarthSpin)
        toggleUserInteraction(true)
      } else {
        this.viewer.scene.postUpdate.removeEventListener(this.makeEarthSpin)
        this.rotatingOrbitalPlanes.show = false
        toggleUserInteraction(false)
      }
      this.viewer.clock.shouldAnimate = true
    },

    disableFullScreen(cesiumContainer, formColumn) {
      document.body.style.overflow = 'auto'
      cesiumContainer.style.position = 'absolute'
      cesiumContainer.style.width = '100%'
      cesiumContainer.style.zIndex = '2'
      cesiumContainer.style.margin = ''
      if (formColumn) {
        cesiumContainer.style.maxHeight = '500px'
        formColumn.classList.remove('fullscreen-form-column')
        formColumn.classList.value = formColumn.classList.value.replace(
          'col-3',
          this.colClass
        )
      }
      if (this.marginClass.length > 0) {
        cesiumContainer.classList.add(this.marginClass)
      }
    },

    checkboxClicked() {
      if (!this.viewerOptions.showFieldOfRegard && this.shouldDialogTrigger) {
        this.viewerOptions.showDialog = true
      } else {
        this.viewerOptions.showFieldOfRegard =
          !this.viewerOptions.showFieldOfRegard
      }
    }
  }
}
</script>

<style scoped>
#cesium-container {
  position: absolute;
  width: 100%;
  height: calc(100% - 0px);
  z-index: 2;
}

#display-options-checkboxes {
  z-index: 3;
  vertical-align: middle;
  display: inline-grid;
  grid-auto-flow: column;
}

#display-options-checkboxes .v-input--selection-controls.v-input {
  background-color: #303336;
  border: 1px solid #444;
  position: relative;
  display: inline-block;
  height: 32px;
  line-height: 32px;
  margin: 2px 3px;
  padding: 2px;
}

#blurred-background {
  background-color: black;
  opacity: 0.5;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 3;
}

#loading-indicator {
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 4;
  display: block;
}
</style>
